import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useAppStore } from '@indipia/shared';
import {
  SiteRegistrationPopup, AssigneeBadges, AssigneeSelect, useCompanyMembers, useDefaultMemberFilter, useMemberFilterState,
} from '@indipia/shared-features';
import { Badge, Button, CollapsibleSection, ErrorState, FilterChip, Input, SearchCombo, Skeleton } from '@indipia/ui-core';
import { photoApi } from '../api/photoApi';
import type { PanelSiteListItem } from '../api/photoApi';
import { siteStatusBadge } from '../utils/siteStatus';

const BASE = '/agents/photo-organizer';

type PanelSiteItem = PanelSiteListItem;

type StatusFilter = 'all' | 'active' | 'progress' | 'done';
type DateMode = 'direct' | 1 | 3 | 6 | 12;

const DATE_MODES: { label: string; mode: DateMode }[] = [
  { label: '직접 설정', mode: 'direct' },
  { label: '1개월', mode: 1 },
  { label: '3개월', mode: 3 },
  { label: '6개월', mode: 6 },
  { label: '1년', mode: 12 },
];

const STATUS_FILTERS: { label: string; filter: StatusFilter }[] = [
  { label: '전체', filter: 'all' },
  { label: '등록', filter: 'active' },
  { label: '진행중', filter: 'progress' },
  { label: '거래완료', filter: 'done' },
];

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

function toYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// 접이식 섹션 타이틀 — 접힌 상태에서 선택값을 타이틀 옆 배지로 모두 표시(group 테마, 인디플래너 RightPanel 요약 배지와 동일 모양)
function SectionTitle({ label, summaries }: { label: string; summaries: string[] }) {
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-02 py-03">
      <span className="shrink-0 text-body-sm font-semibold text-text-muted">{label}</span>
      {summaries.map((s, i) => (
        <span key={`${i}-${s}`} className="rounded-full bg-group px-03 text-caption font-semibold text-group-onPrimary">{s}</span>
      ))}
    </span>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

/**
 * @param onSelectSite 지정하면 현장을 누를 때 상세 화면으로 이동하지 않고 이 콜백만 호출한다 — 공유 발송 화면처럼
 *                     패널로 "작업 대상 현장"을 고르는 화면이 쓴다. 미지정이면 기존대로 현장 상세로 이동.
 */
export function SitePanel({ currentSiteId, onSelectSite }: {
  currentSiteId?: number | null;
  onSelectSite?: (item: PanelSiteItem) => void;
}) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [items, setItems] = useState<PanelSiteItem[]>([]);
  const [loading, setLoading] = useState(false);
  // 조회 실패 — 빈 목록과 구분해 오류 상태로 보인다(D-07 §3.2 #8)
  const [loadError, setLoadError] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState('');

  const [dateOpen, setDateOpen] = useState(false);
  const [dateMode, setDateMode] = useState<DateMode>('direct');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // 담당자·관리자 조건(2026-10-12 개선사항 D-6) — 기본값: 직원은 나, 사장님은 조건 없음(전체 현장).
  // 지금 보고 있는 현장(currentSiteId)은 조건과 무관하게 항상 목록에 포함(includeSiteId).
  const { myId, defaultIds } = useDefaultMemberFilter();
  const { data: companyMembers = [] } = useCompanyMembers();
  const [memberOpen, setMemberOpen] = useState(false);
  const [memberIds, setMemberIds] = useMemberFilterState();
  const memberIdsKey = memberIds.join(',');
  const isDefaultMembers = memberIds.length === defaultIds.length && memberIds.every(v => defaultIds.includes(v));
  const memberNames = memberIds.map(id => companyMembers.find(m => m.memberId === id)?.memberName ?? (id === myId ? '나' : `회원 ${id}`));

  // 접힌 섹션 타이틀 옆 선택값 요약(날짜·상태·담당자 공통) — 인디플래너 RightPanel 요약 배지와 같은 모양
  const dateSummary = dateMode !== 'direct'
    ? DATE_MODES.find(m => m.mode === dateMode)?.label
    : (dateFrom || dateTo) ? `${dateFrom} ~ ${dateTo}` : undefined;
  const statusSummary = statusFilter !== 'all' ? STATUS_FILTERS.find(f => f.filter === statusFilter)?.label : undefined;
  // 조회 조건이 하나라도 걸려 있으면 빈 목록 문구를 '조건에 맞는 현장이 없습니다.'로
  const hasCondition = !!(keyword || dateFrom || dateTo) || statusFilter !== 'all' || memberIds.length > 0;

  const [version, setVersion] = useState(0);
  const [siteRegOpen, setSiteRegOpen] = useState(false);

  const fetchList = useCallback(() => {
    if (!isOpen) return;
    setLoading(true);
    setLoadError(false);
    photoApi.getPanelSiteList({
      keyword:    keyword    || undefined,
      dateFrom:   dateFrom   || undefined,
      dateTo:     dateTo     || undefined,
      siteStatus: statusFilter === 'all' ? undefined
                : statusFilter === 'active'   ? 'ACTIVE'
                : statusFilter === 'progress' ? 'PROGRESS'
                : 'DONE',
      memberIds:     memberIds.length ? memberIds : undefined,
      includeSiteId: currentSiteId ?? undefined,
    })
      .then(res => setItems(res.data?.data?.items ?? []))
      .catch((err) => { console.error(err); setItems([]); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [isOpen, keyword, dateFrom, dateTo, statusFilter, version, memberIdsKey, currentSiteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchList(); }, [fetchList]);

  // 현장 선택 모드(onSelectSite)에서 아직 고른 현장이 없으면 인디플래너 공유 화면처럼 목록의 첫 번째 현장을 자동으로 선택한다.
  // 진입 후 처음 불러온 필터 없는 목록에서 한 번만 — 사용자가 검색·필터를 바꾼 뒤의 결과로는 대신 고르지 않는다.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current || !onSelectSite || currentSiteId != null || loading) return;
    if (keyword || dateFrom || dateTo || statusFilter !== 'all' || !isDefaultMembers) return;
    if (items.length === 0) return;
    autoSelectedRef.current = true;
    onSelectSite(items[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, loading, currentSiteId, keyword, dateFrom, dateTo, statusFilter, isDefaultMembers]);

  function handleDateMode(mode: DateMode) {
    setDateMode(mode);
    if (mode === 'direct') { setDateFrom(''); setDateTo(''); return; }
    const now = new Date();
    const from = new Date(now);
    from.setMonth(from.getMonth() - (mode as number));
    setDateFrom(toYMD(from));
    setDateTo(toYMD(now));
  }

  return (
    // 인디플래너(scheduler) RightPanel과 동일한 표준 패턴(원래 shell SiteArea.tsx 기획서 패턴):
    // 바깥 래퍼는 폭 트랜지션만 담당(w-0/w-[308px]), 토글 버튼은 왼쪽 끝에 절대위치로 항상 노출,
    // 실제 내용은 열렸을 때만 렌더되는 안쪽 div(h-full)가 담당한다.
    <div className={`relative h-full shrink-0 overflow-visible transition-[width] duration-200 ${isOpen ? 'w-[308px]' : 'w-0'}`}>
      {/* 원형 토글 버튼 — 항상 표시, 패널 왼쪽 끝에 고정 */}
      <button
        type="button"
        onClick={() => setIsOpen(v => !v)}
        aria-label={isOpen ? '현장 목록 닫기' : '현장 목록 열기'}
        className="absolute -left-3.5 top-4 z-20 flex h-7 w-7 items-center justify-center rounded-full border border-surface-raised bg-group text-group-onPrimary shadow-md transition-transform duration-150 hover:scale-110 hover:opacity-90"
      >
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className={`transition-transform duration-200 ${isOpen ? '' : 'rotate-180'}`}>
          <polyline points="4 4 7 8 4 12" />
          <polyline points="9 4 12 8 9 12" />
        </svg>
      </button>

      {isOpen && (
        <div className="flex h-full w-[308px] flex-col border-l border-border bg-surface-raised">
          {/* 헤더 */}
          <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-3">
            <span className="text-sm font-semibold text-text">현장 목록</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setSiteRegOpen(true)}
              className="!h-auto shrink-0 gap-1 !px-2.5 !py-1 text-[length:var(--type-caption-size)]"
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              새 현장
            </Button>
          </div>

          {/* search */}
          <div className="flex shrink-0 border-b border-border px-2.5 py-2">
            <SearchCombo
              size="sm"
              value={searchInput}
              onChange={v => { setSearchInput(v); if (!v) setKeyword(''); }}
              onSearch={v => setKeyword(v.trim())}
              onClear={() => setKeyword('')}
              placeholder="현장명, 주소, 고객명"
            />
          </div>

          {/* date filter */}
          <div className="shrink-0 border-b border-border">
            <CollapsibleSection
              title={<SectionTitle label="날짜" summaries={!dateOpen && dateSummary ? [dateSummary] : []} />}
              open={dateOpen}
              onToggle={() => setDateOpen(v => !v)}
              className="px-3"
            >
              <div className="-mx-3 flex flex-col gap-2 bg-surface-raised px-2.5 py-2">
                <div className="flex flex-wrap gap-1">
                  {DATE_MODES.map(({ label, mode }) => (
                    <FilterChip
                      key={String(mode)}
                      active={dateMode === mode}
                      onClick={() => handleDateMode(mode)}
                      size="md"
                    >
                      {label}
                    </FilterChip>
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={e => { setDateMode('direct'); setDateFrom(e.target.value); }}
                    size="sm"
                    width="fill"
                    className="min-w-0 flex-1"
                  />
                  <span className="shrink-0 text-body-sm text-text-disabled">~</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={e => { setDateMode('direct'); setDateTo(e.target.value); }}
                    size="sm"
                    width="fill"
                    className="min-w-0 flex-1"
                  />
                </div>
              </div>
            </CollapsibleSection>
          </div>

          {/* status filter */}
          <div className="shrink-0 border-b border-border">
            <CollapsibleSection
              title={<SectionTitle label="상태" summaries={!statusOpen && statusSummary ? [statusSummary] : []} />}
              open={statusOpen}
              onToggle={() => setStatusOpen(v => !v)}
              className="px-3"
            >
              <div className="-mx-3 flex flex-wrap gap-1 bg-surface-raised px-2.5 py-2">
                {STATUS_FILTERS.map(({ label, filter }) => (
                  <FilterChip
                    key={filter}
                    active={statusFilter === filter}
                    onClick={() => setStatusFilter(filter)}
                    size="md"
                  >
                    {label}
                  </FilterChip>
                ))}
              </div>
            </CollapsibleSection>
          </div>

          {/* 담당자·관리자 filter (D-6) — 접힌 상태에서는 선택 직원을 배지로 모두 표시 */}
          <div className="shrink-0 border-b border-border">
            <CollapsibleSection
              title={<SectionTitle label="담당자·관리자" summaries={memberOpen ? [] : memberNames} />}
              open={memberOpen}
              onToggle={() => setMemberOpen(v => !v)}
              className="px-04"
            >
              <div className="-mx-04 bg-surface-raised px-03 py-03">
                <AssigneeSelect
                  members={companyMembers}
                  values={memberIds}
                  onChange={setMemberIds}
                  myId={myId}
                />
              </div>
            </CollapsibleSection>
          </div>

          {/* site card list */}
          <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-2">
            {loading && items.length === 0 ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} height={78} className="rounded-sm" />
              ))
            ) : loadError ? (
              <ErrorState description="현장 목록을 불러올 수 없어요. 잠시 후 다시 시도해주세요." onRetry={fetchList} />
            ) : items.length === 0 ? (
              <div className="flex flex-1 items-center justify-center pt-12">
                <span className="text-body-sm text-text-disabled">{hasCondition ? '조건에 맞는 현장이 없습니다.' : '현장이 없습니다'}</span>
              </div>
            ) : items.map(item => {
              const isActive = item.siteId === currentSiteId;
              // 현장 선택 모드(공유 발송 등)에서는 인디플래너 우측 패널과 같이 선택된 현장만 강조하고 나머지는 회색 면으로 낮춘다
              // (opacity는 글자 대비를 떨어뜨려 회색 배경으로 처리). 아무것도 고르지 않았을 땐 전부 일반 상태로 둬 선택을 유도한다.
              const isDimmed = !!onSelectSite && currentSiteId != null && !isActive;
              return (
                <div
                  key={item.siteId}
                  role="button"
                  tabIndex={0}
                  onClick={() => { if (!isActive) (onSelectSite ? onSelectSite(item) : navigate(`${BASE}/site/${item.siteId}`)); }}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!isActive) (onSelectSite ? onSelectSite(item) : navigate(`${BASE}/site/${item.siteId}`)); } }}
                  className={`flex shrink-0 rounded-md border bg-surface-raised transition-colors ${isActive ? 'cursor-default border-group' : 'cursor-pointer border-border hover:border-group'}`}
                  style={isActive ? { background: 'color-mix(in srgb, var(--gt-primary) 5%, transparent)' } : isDimmed ? { background: 'var(--shell-bg)' } : undefined}
                >
                  {/* 현장 색상 바 */}
                  <div className="w-[5px] shrink-0 self-stretch rounded-bl-md rounded-tl-md" style={{ background: isActive ? 'var(--gt-primary)' : isDimmed ? 'var(--gray-300)' : (item.siteColor ?? 'var(--shell-border)') }} />

                  <div className="min-w-0 flex-1 px-3 py-2.5">
                    <div className="mb-1 flex items-center justify-between gap-1">
                      <span className="flex-1 truncate text-[length:var(--type-caption-size)] font-bold text-text">
                        {item.siteName ?? item.address}
                      </span>
                      <Badge status={siteStatusBadge(item.siteStatus)} className="shrink-0 whitespace-nowrap !text-[10px]">
                        {item.siteStatusName ?? item.siteStatus}
                      </Badge>
                    </div>
                    <p className="mb-1.5 truncate text-[length:var(--type-caption-size)] leading-snug text-text-secondary">
                      {item.customerName}
                    </p>
                    {item.address && (
                      <div className="mb-1.5 flex items-center gap-1 text-[10.5px] text-text-disabled">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                        </svg>
                        <span className="truncate">{item.address}</span>
                      </div>
                    )}
                    {/* 담당자 · 관리자 — 한 줄(D-6). 배지 클릭은 카드 클릭과 별개 */}
                    <AssigneeBadges
                      className="mb-02"
                      wrap={false}
                      chargeMembers={item.chargeMembers}
                      managerMembers={item.managerMembers}
                      filterIds={memberIds}
                      myId={myId}
                    />
                    <div className="flex items-center justify-end">
                      <span className="text-[10.5px] text-text-disabled">
                        {formatDate(item.createdAt)} 등록
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {siteRegOpen && (
        <SiteRegistrationPopup
          agentId={useAppStore.getState().subscriptions.find(s => s.agentType === 'A01')?.agentId ?? 1}
          onClose={() => setSiteRegOpen(false)}
          onComplete={(siteId) => {
            setSiteRegOpen(false);
            setVersion(v => v + 1);
            navigate(`${BASE}/site/${siteId}`);
          }}
        />
      )}
    </div>
  );
}
